/**
 * Раздел «Клиники».
 *
 * Карточка клиники имеет два состояния: ПРОСМОТР и РЕДАКТИРОВАНИЕ. Раньше
 * правка включалась щелчком по значению — попытка развернуть карточку попадала
 * по названию и превращала его в поле ввода. Просмотр и правка должны включаться
 * разными действиями, а не одним и тем же.
 *
 * В режиме просмотра реквизиты показаны как справка: строкой, сгруппированно,
 * без полей ввода. Кнопка «Изменить» переводит карточку целиком в правку —
 * можно поменять всё сразу, а не по одному полю.
 *
 * Правки остаются черновиком до кнопки «Сохранить» (§6): «Готово»
 * лишь выключает режим правки, в базу ничего не отправляет.
 */

import { useEffect, useMemo, useState } from 'react';
import { ProfileFact } from '../components/ProfileDetails.js';
import { ClinicContractLink } from '../components/ContractCreate.js';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, Truck, Rows3, UserRoundX, Building2, Check, FileText, Mail, MapPin, Pencil, Plus, Search, Trash2, X } from 'lucide-react';
import { ClinicIcon, DoctorIcon } from '../components/icons.js';
import {
  formatDateDots,
  contractText,
  legalTitle,
  legalTitleText,
  pluralRu,
  signerInPersonOf,
  signerSignatureOf,
  rublesToKop,
} from '@otc/shared';
import { useBootstrap } from '../api/queries.js';
import { useDrafts } from '../store/drafts.js';
import { useSync } from '../store/useSync.js';
import { useModule } from '../store/useModule.js';
import { Button, Field, SectionHeader, Spinner, Toast, useToast } from '../components/ui.js';
import { ConflictModal } from '../components/GuardModals.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { SaveDiscard } from '../components/SaveDiscard.js';
import { Card, CardChips, CardFact, CardGrid, CardLinks, Chip, Row, Пометка } from '../components/Cards.js';
import { CardWindow } from '../components/CardWindow.js';
import { useFillHeight } from '../components/useFillHeight.js';
import { useTableZoom } from '../components/useTableZoom.js';
import { ZoomControl } from '../components/ZoomControl.js';
import { twoGisLink } from '../components/twoGis.js';
import { ToggleSetting } from '../components/Toggle.js';
import { DoctorsForClinicPicker } from '../components/DoctorsForClinicPicker.js';

interface ClinicRow {
  id: string; version: number; name: string; doctors: string[];
  countDelivery?: boolean; deliveryKop?: number;
}

interface LegalRow {
  id: string; version: number; clinicId: string; name: string;
  form: string; shortForm: string; pay: 'По договору' | 'Наличные';
  contractNo: string; contractDate: string; contract: string; email: string;
  inn: string; ogrn: string; kpp: string; address: string;
  bank: string; bik: string; rs: string; ks: string; note: string;
  /** Исходная строка учёта. Оставлена для сверки, документами не используется. */
  signer: string;
  signerPosition: string;
  signerName: string;
  signerGenitive: string;
  warnOnReady: boolean;
  mergeInvoiceItems: boolean;
  /** Фамилии в строке счёта: пациент (по умолчанию есть) и врач (по умолчанию нет). */
  invoicePatient: boolean;
  invoiceDoctor: boolean;
}

interface BranchRow {
  id: string; version: number; clinicId: string; legalId: string;
  name: string;
  /** Фактический адрес точки — отдельно от юридического адреса юрлица. */
  address: string;
}


/**
 * Название юрлица. Незаполненное показывается приглушённой подсказкой —
 * пустой заголовок читался как потерянные данные.
 */
function LegalTitle({
  legal,
  className = '',
}: {
  legal: { name: string; shortForm: string };
  className?: string;
}) {
  const title = legalTitle(legal);
  return (
    <span className={`${className} ${title.placeholder ? 'italic text-ink2' : 'text-ink'}`}>
      {title.text}
    </span>
  );
}

export function Clinics() {
  const bootstrap = useBootstrap(true);
  const module = useModule('clinics');
  const { toast, show } = useToast();

  /** Открытая карточка. Подробности живут в окне, а не в плитке. */
  const [openId, setOpenId] = useState<string | null>(null);
  /** Карточки в режиме правки. Просмотр и правка — разные состояния. */
  const [editing, setEditing] = useState<Record<string, boolean>>({});
  const [query, setQuery] = useState('');
  const [confirmClinic, setConfirmClinic] = useState<string | null>(null);

  /**
   * Переход из «Документы → Договоры»: адрес вида /clinics?edit=<id> сразу
   * открывает карточку в правке. Договор и реквизиты лежат здесь, а искать
   * нужную клинику среди трёх десятков плиток руками — лишняя работа.
   *
   * Параметр снимается с адреса сразу после открытия: иначе обновление
   * страницы или возврат назад снова распахивали бы ту же карточку.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const target = searchParams.get('edit');
    if (!target) return;
    setOpenId(target);
    setEditing((current) => ({ ...current, [target]: true }));
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const allLegals = useMemo(
    () => bootstrap.data?.clinics.flatMap((c) => c.legals),
    [bootstrap.data?.clinics],
  );
  const allBranches = useMemo(
    () => bootstrap.data?.clinics.flatMap((c) => c.branches),
    [bootstrap.data?.clinics],
  );

  useSync('clinics', 'clinic', bootstrap.data?.clinics as never);
  useSync('clinics', 'legal', allLegals as never);
  useSync('clinics', 'branch', allBranches as never);

  const draftState = useDrafts((state) => state.modules.clinics);
  const clinics = useMemo(() => {
    void draftState;
    return useDrafts.getState().entities<ClinicRow>('clinics', 'clinic');
  }, [draftState]);
  const legals = useMemo(() => {
    void draftState;
    return useDrafts.getState().entities<LegalRow>('clinics', 'legal');
  }, [draftState]);
  const branches = useMemo(() => {
    void draftState;
    return useDrafts.getState().entities<BranchRow>('clinics', 'branch');
  }, [draftState]);

  const doctors = bootstrap.data?.doctors ?? [];

  const update = (kind: string, id: string, patch: Record<string, unknown>) =>
    useDrafts.getState().update('clinics', kind, id, patch);

  /**
   * Сохранение раздела.
   *
   * `оставитьПравку` — для кнопки в самой карточке: после сохранения она
   * должна остаться открытой и в том же состоянии, иначе правка обрывается
   * на полуслове. Кнопка в шапке, наоборот, завершает работу со списком и
   * режим правки выключает.
   */
  const save = async ({ оставитьПравку = false }: { оставитьПравку?: boolean } = {}): Promise<boolean> => {
    const outcome = await module.save();
    if (outcome.ok) {
      /* Молча: сохранение — не событие, о котором стоит сообщать. */
      if (!оставитьПравку) setEditing({});
      return true;
    }
    if (outcome.blocked) show(outcome.blocked[0]!.message, 'error');
    else if (outcome.error) show(outcome.error, 'error');
    return false;
  };

  /**
   * «Готово» в карточке: сохранить и выйти из правки.
   *
   * Раньше здесь стояли две кнопки рядом — «Готово» и «Сохранить», — и это
   * было два нажатия на одно намерение: закончил править. Теперь «Готово»
   * само отправляет правки в базу.
   *
   * Если сохранить не удалось (расхождение версий, отказ сервера), карточка
   * ОСТАЁТСЯ в правке: иначе правки исчезли бы с глаз, оставшись
   * несохранёнными.
   */
  const завершитьПравку = async (id: string) => {
    if (module.dirty && !(await save({ оставитьПравку: true }))) return;
    setEditing((current) => ({ ...current, [id]: false }));
  };

  /**
   * Новая клиника создаётся с ПУСТЫМИ полями.
   *
   * Заготовки вроде «Новая клиника» и «ООО» приходилось сначала выделять
   * и удалять, прежде чем ввести своё, — а незамеченная заготовка попадала
   * в документы. Пустое поле с подсказкой ничего не навязывает.
   */
  const addClinic = () => {
    const clinicId = useDrafts.getState().create('clinics', 'clinic', { name: '', doctors: [] });
    const legalId = useDrafts.getState().create('clinics', 'legal', {
      clinicId, name: '', form: '', shortForm: '', pay: 'По договору',
      contractNo: '', contractDate: '', contract: '',
      email: '', inn: '', ogrn: '', kpp: '', address: '', bank: '', bik: '', rs: '', ks: '',
      signer: '', signerPosition: '', signerName: '', signerGenitive: '',
      note: '', warnOnReady: false, mergeInvoiceItems: false,
      invoicePatient: true, invoiceDoctor: false,
    });
    useDrafts.getState().create('clinics', 'branch', { clinicId, legalId, name: '', address: '' });

    // Новая клиника сразу открыта и в правке — её всё равно нужно заполнять.
    setOpenId(clinicId);
    setEditing((current) => ({ ...current, [clinicId]: true }));
    show('Клиника добавлена — заполните реквизиты юрлица');
  };

  const openClinic = openId ? clinics.find((c) => c.id === openId) : undefined;
  const clinicToDelete = confirmClinic ? clinics.find((c) => c.id === confirmClinic) : undefined;

  /**
   * Закрытие карточки возвращает её в просмотр.
   *
   * Иначе режим правки запоминался за клиникой: открыл, нажал «Изменить»,
   * закрыл — и в следующий раз карточка открывалась сразу формой, минуя
   * просмотр. Правки при этом не теряются: они живут в черновике раздела
   * и ждут кнопку «Сохранить изменения».
   */
  const closeCard = () => {
    if (openId) setEditing((current) => ({ ...current, [openId]: false }));
    setOpenId(null);
  };

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q ? clinics.filter((c) => c.name.toLowerCase().includes(q)) : clinics;
    return [...list].sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  }, [clinics, query]);

  /* Листается список, а не страница: шапка раздела остаётся на месте. */
  const { ref: списокRef, высота: высотаСписка } = useFillHeight();

  /* Масштаб плиток: у каждого раздела свой, запоминается в браузере. */
  const zoom = useTableZoom('clinics', true);

  if (bootstrap.isLoading) return <Spinner />;

  return (
    <div>
      <SectionHeader
        icon={ClinicIcon}
        title="Клиники"
        subtitle={`${clinics.length} клиник · ${legals.length} юрлиц · ${branches.length} филиалов`}
        right={
          <>
            {/*
              Поиск и «Добавить» ОТДЕЛЕНЫ от сохранения.

              Это работа со списком: найти запись, завести новую. «Сохранить»
              и «Отменить» относятся ко всему разделу разом и стоят особняком —
              в общем ряду через одинаковый зазор четыре кнопки читались как
              равные, и рука шла не туда.
            */}
            <span
              className="flex items-center gap-2 md:mr-2 md:border-r md:pr-4"
              style={{ borderColor: 'var(--otc-line)' }}
            >
              {/* Лупа внутри поля — как во «Врачах» и «Конструкциях».
                  Обёртка держит ту же ширину, что держало само поле. */}
              <span className="relative block w-36 md:w-44">
                <Search
                  size={14}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
                  style={{ color: 'var(--otc-ink2)' }}
                />
                <input
                  className="w-full rounded-lg border px-3 py-1.5 text-sm"
                  style={{
                    borderColor: 'var(--otc-line)',
                    background: 'var(--otc-surface)',
                    paddingLeft: '2.1rem',
                  }}
                  placeholder="Поиск…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </span>
              <Button onClick={addClinic}>
                <Plus size={14} /> Добавить
              </Button>
              {/* Масштаб плиток — рядом с поиском: и то и другое про список. */}
              <ZoomControl zoom={zoom} hint="Мельче — на экран влезет больше клиник" />
            </span>
            <SaveDiscard
              dirty={module.dirty}
              saving={module.saving}
              onSave={() => void save()}
              onDiscard={() => {
                module.discard();
                show('Изменения отменены');
              }}
              what="Клиники"
            />
          </>
        }
      />

      {/*
        Плитки, а не строки во всю ширину. При 35 клиниках список из
        полноширинных строк — это несколько экранов прокрутки ради того, чтобы
        найти одну; в сетке они видны все сразу.

        Подробности открываются отдельным окном: раскрытие внутри сетки
        растягивало бы ряд по высоте самой длинной карточки и ломало раскладку.
      */}
      {/*
        Листаются ПЛИТКИ, а не страница: поиск и «Добавить клинику» остаются
        на месте на любой прокрутке. Подробности — в `useFillHeight`.

        Небольшое поле по краям — чтобы тени плиток не срезались границей
        прокручиваемого блока.
      */}
      <div
        ref={списокRef}
        className="-mx-1 px-1"
        style={{ maxHeight: высотаСписка ?? undefined, overflowY: высотаСписка ? 'auto' : undefined }}
      >
        {/* Масштаб — на содержимом: высота блока подобрана под окно, и
            уменьшать её вместе с плитками нельзя. */}
        <div style={{ zoom: `${zoom.zoom}%` }}>
        <CardGrid>
        {visible.map((clinic) => {
          const clinicLegals = legals.filter((l) => l.clinicId === clinic.id);
          const clinicBranches = branches.filter((b) => b.clinicId === clinic.id);
          const linkedIds = clinic.doctors ?? [];
          const clinicDoctors = doctors.filter((d) => !d.archived && linkedIds.includes(d.id));

          return (
            <Card
              key={clinic.id}
              title={clinic.name}
              icon={ClinicIcon}
              subtitle={`${clinicBranches.length} ${pluralRu(clinicBranches.length, ['филиал', 'филиала', 'филиалов'])} · ${clinicLegals.length} ${pluralRu(clinicLegals.length, ['юрлицо', 'юрлица', 'юрлиц'])}`}
              /* Повторный двойной щелчок по той же плитке закрывает карточку. */
              onOpen={() => setOpenId((id) => (id === clinic.id ? null : clinic.id))}
              onDelete={() => setConfirmClinic(clinic.id)}
              deleteTitle="Удалить клинику"
            >
              {/*
                Фактические адреса филиалов — по ним везут работу, и это
                первое, что спрашивают у администратора. Юридический адрес
                из реквизитов для этого не годится: он часто другой.
              */}
              <div className="mt-2 space-y-1">
                {clinicBranches.map((branch) => (
                  <CardFact
                    key={branch.id}
                    icon={MapPin}
                    // Название точки нужно, только когда филиалов несколько.
                    prefix={clinicBranches.length > 1 ? branch.name || undefined : undefined}
                    value={branch.address}
                    empty="адрес не указан"
                  />
                ))}
              </div>

              {/*
                Почта видна, не открывая карточку: на неё уходят счета.
                Показана СПОКОЙНО, без красного: почты нет почти ни у кого,
                и подсветка каждой карточки превращала бы её в фон, который
                перестают замечать.
              */}
              <div className="mt-1 space-y-1">
                {clinicLegals.map((legal) => (
                  <CardFact
                    key={legal.id}
                    icon={Mail}
                    prefix={clinicLegals.length > 1 ? legal.name : undefined}
                    value={legal.email}
                    empty="почта не указана"
                    breakAll
                  />
                ))}
              </div>

              {/*
                Врачи прямо на плитке. Фамилия врача — то, по чему клинику
                чаще всего и опознают: заказ приходит «от Лебедевой», а не
                «из Стомцентра».
              */}
              <CardLinks
                icon={DoctorIcon}
                names={clinicDoctors.map((d) => d.name)}
                empty="врачи не привязаны"
              />

              <CardChips>
                {/*
                  Плитка показывает ВСЕ особые пометки юрлиц — те же, что в
                  карточке: иначе порядок счёта клиники виден, только если её
                  открыть, и о нём легко забыть при выставлении. Обычное
                  (оплата, пациент в счёте) не пишется — это шум.

                  Напоминание о счёте при «одной строке» не действует, поэтому
                  и не показывается.
                */}
                {clinicLegals.some((l) => l.warnOnReady && !l.mergeInvoiceItems) && <Chip warn icon={AlertTriangle}>напоминать о счёте</Chip>}
                {clinicLegals.some((l) => l.mergeInvoiceItems) && <Chip info icon={Rows3}>счёт одной строкой</Chip>}
                {clinic.countDelivery && <Chip color="#7c3aed" icon={Truck}>доставка в счёте</Chip>}
                {clinicLegals.some((l) => !l.mergeInvoiceItems && l.invoiceDoctor) && <Chip info icon={DoctorIcon}>врач в счёте</Chip>}
                {clinicLegals.some((l) => !l.mergeInvoiceItems && l.invoicePatient === false) && (
                  <Chip info icon={UserRoundX}>без пациента в счёте</Chip>
                )}
                {/* Красным — только то, из-за чего клиника нерабочая. */}
                {clinicLegals.length === 0 && <Chip danger icon={Building2}>нет юрлиц</Chip>}
              </CardChips>
            </Card>
          );
        })}
        </CardGrid>

        {visible.length === 0 && <p className="py-8 text-center text-sm text-ink2">Ничего не нашли</p>}
        </div>
      </div>

      {/* Карточка клиники: просмотр и правка — разные состояния, разные кнопки */}
      {/*
        Карточка клиники — ПОДВИЖНОЕ ОКНО, как карточка заказа.

        Прежде это было модальное окно с затемнением: список клиник под ним
        гас и не работал. Теперь окно отодвигают и растягивают, а плитки
        позади остаются видимыми.
      */}
      {openClinic && (
        <CardWindow
          ключ="otc.clinicWindow.v2"
          title={openClinic.name || 'Новая клиника'}
          onClose={closeCard}
          /*
           * Несколько юрлиц встают в две колонки, поэтому окно шире. Иначе
           * карточки складывались в высокую стопку: у клиники с одним юрлицом
           * всё помещалось на экран, а у клиники с двумя приходилось
           * прокручивать — при одинаковом содержимом.
           */
          /* Шире прежних 1000/1280: текст в карточке стал крупнее. */
          ширина={legals.filter((l) => l.clinicId === openClinic.id).length > 1 ? 1400 : 1120}
          footer={
            <>
              {/*
                Две кнопки, а не три: «Изменить» — начать правку, «Готово» —
                закончить и сохранить. Отдельной «Сохранить» здесь больше нет:
                она означала то же самое, что «Готово», и заставляла нажимать
                дважды. Кнопка в шапке раздела осталась — она сохраняет весь
                раздел разом, включая правки в других карточках.
              */}
              <Button
                variant={editing[openClinic.id] ? 'primary' : 'secondary'}
                disabled={module.saving}
                onClick={() =>
                  editing[openClinic.id]
                    ? void завершитьПравку(openClinic.id)
                    : setEditing((e) => ({ ...e, [openClinic.id]: true }))
                }
              >
                {editing[openClinic.id] ? <Check size={14} /> : <Pencil size={14} />}
                {editing[openClinic.id] ? (module.saving ? 'Сохранение…' : 'Готово') : 'Изменить'}
              </Button>

              <Button variant="ghost" onClick={closeCard}>Закрыть</Button>
            </>
          }
        >
          <div className="pb-4">
            {editing[openClinic.id] ? (
              <ClinicEdit
                clinic={openClinic}
                legals={legals.filter((l) => l.clinicId === openClinic.id)}
                branches={branches.filter((b) => b.clinicId === openClinic.id)}
                // Архивных врачей к клинике не привязывают; уже привязанные остаются видны.
                doctors={doctors.filter((d) => !d.archived || (openClinic.doctors ?? []).includes(d.id))}
                linkedIds={openClinic.doctors ?? []}
                onUpdate={update}
              />
            ) : (
              <ClinicView
                clinic={openClinic}
                legals={legals.filter((l) => l.clinicId === openClinic.id)}
                branches={branches.filter((b) => b.clinicId === openClinic.id)}
                doctors={doctors.filter((d) => !d.archived && (openClinic.doctors ?? []).includes(d.id))}
              />
            )}
          </div>
        </CardWindow>
      )}

      {/* Подтверждение удаления клиники — одно окно на весь раздел */}
      {clinicToDelete && (
        <ConfirmDialog
          title={`Удалить клинику «${clinicToDelete.name}»?`}
          description="Клиника исчезнет из справочника вместе со всеми юрлицами и филиалами."
          details={
            <>
              <p>
                Юрлиц: {legals.filter((l) => l.clinicId === clinicToDelete.id).length} ·{' '}
                филиалов: {branches.filter((b) => b.clinicId === clinicToDelete.id).length}
              </p>
              <p className="mt-1 text-xs text-ink2">
                Если на клинику ссылаются заказы, сервер откажет при сохранении и назовёт их число —
                данные не потеряются.
              </p>
            </>
          }
          confirmLabel="Удалить клинику"
          onConfirm={() => {
            useDrafts.getState().remove('clinics', 'clinic', clinicToDelete.id);
            setConfirmClinic(null);
            if (openId === clinicToDelete.id) closeCard();
            show(`Клиника «${clinicToDelete.name}» помечена к удалению — нажмите «Сохранить»`);
          }}
          onCancel={() => setConfirmClinic(null)}
        />
      )}

      {module.conflicts && (
        <ConflictModal
          conflicts={module.conflicts}
          onReload={() => {
            module.discard();
            module.dismissConflicts();
            setEditing({});
            void bootstrap.refetch();
          }}
          onKeepMine={() => void module.keepMine()}
          onClose={module.dismissConflicts}
        />
      )}

      <Toast toast={toast} />
    </div>
  );
}

/**
 * Что уйдёт в акт — показано прямо под полями подписанта.
 *
 * Родительный падеж выводится правилами склонения, а они покрывают не всё:
 * «Приходько» и «Лю» не склоняются, женская фамилия на согласный не меняется,
 * а доверенность правилами не описывается вовсе. Поэтому результат виден
 * здесь, до отправки, и его можно заменить своей формулировкой.
 */
function SignerPreview({
  legal,
  onOverride,
}: {
  legal: LegalRow;
  onOverride: (value: string) => void;
}) {
  const [manual, setManual] = useState(legal.signerGenitive.trim().length > 0);

  const inPerson = signerInPersonOf(legal);
  const signature = signerSignatureOf(legal);

  if (!legal.signerPosition.trim() && !legal.signerName.trim() && !manual) {
    return (
      <p className="rounded-lg border border-dashed px-3 py-2 text-xs text-ink2" style={{ borderColor: 'var(--otc-line)' }}>
        Подписант не задан — в акте не будет оборота «в лице …». Так оформляются акты ИП,
        действующего лично.
      </p>
    );
  }

  return (
    <div className="rounded-lg border px-3 py-2.5" style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-bg)' }}>
      <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-ink2">В акте будет</p>

      <p className="text-xs text-ink">
        …в лице <span className="font-medium">{inPerson || '—'}</span>, ОГРН…
      </p>
      <p className="mt-1 text-xs text-ink">
        ____________ <span className="font-medium">{signature || '—'}</span>
      </p>

      {manual ? (
        <div className="mt-2.5">
          <Field
            label="Свой оборот «в лице …»"
            hint="Без слов «в лице» — их подставит документ. Сюда же пишется доверенность"
          >
            <input
              className="otc-input"
              value={legal.signerGenitive}
              onChange={(e) => onOverride(e.target.value)}
              placeholder="Должность и ФИО так, как должно стоять в акте"
            />
          </Field>
          <button
            onClick={() => {
              onOverride('');
              setManual(false);
            }}
            className="mt-1.5 text-[11px] underline text-ink2"
          >
            Вернуться к автоматической подстановке
          </button>
        </div>
      ) : (
        <button
          onClick={() => setManual(true)}
          className="mt-2 text-[11px] underline"
          style={{ color: 'var(--otc-accent)' }}
        >
          Записать оборот вручную
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Просмотр
// ---------------------------------------------------------------------------

function ClinicView({ clinic, legals, branches, doctors }: {
  clinic: ClinicRow; legals: LegalRow[]; branches: BranchRow[]; doctors: { id: string; name: string }[];
}) {
  return <div className="profile-view">
    <div className="profile-overview">
      <section className="profile-panel">
        <h3 className="profile-heading"><Mail size={18} aria-hidden="true"/>Связь и адрес</h3>
        <div className="profile-stack">
          {legals.map(legal=><div key={legal.id}>
            <p className="profile-label">Почта для счетов{legals.length>1 && <> · {legalTitleText(legal)}</>}</p>
            {legal.email ? <a className="profile-link" href={`mailto:${legal.email.split(/[,;\s]+/).filter(Boolean).join(',')}`}>{legal.email}</a> : <span className="text-ink2">Не указана</span>}
          </div>)}
          {branches.map(branch=><div key={branch.id} className="profile-branch">
            <p className="profile-label">Филиал · {branch.name || 'Без названия'}</p>
            {legals.length>1 && <p className="profile-label">{legalTitleText(legals.find(l=>l.id===branch.legalId) ?? {name:'Юрлицо не указано',shortForm:''})}</p>}
            {branch.address ? <a href={twoGisLink(branch.address)} target="_blank" rel="noreferrer" className="profile-map" title="Открыть адрес в 2ГИС"><img src="/2gis.png" alt="2ГИС" width={20} height={20}/><span>{branch.address}</span></a> : <p className="text-ink2">Фактический адрес не указан</p>}
          </div>)}
          {branches.length===0 && <p className="text-ink2">Филиалы не указаны</p>}
        </div>
      </section>
      <section className="profile-panel">
        <h3 className="profile-heading"><DoctorIcon size={18}/>Врачи <span className="profile-count">{doctors.length}</span></h3>
        <div className="profile-stack">
          {doctors.map(doctor=><div key={doctor.id} className="profile-person"><span className="profile-avatar" aria-hidden="true"><DoctorIcon size={18}/></span><span>{doctor.name || 'Без имени'}</span></div>)}
          {doctors.length===0 && <p className="text-ink2">Ни один врач не привязан к этой клинике</p>}
        </div>
      </section>
    </div>
    {legals.map(legal=><section key={legal.id} className="profile-panel">
      <div className="profile-legal-title"><LegalTitle legal={legal} className="font-semibold"/><span className="profile-badge">{legal.pay}</span></div>
      <div className="profile-settings">
        {legal.warnOnReady && !legal.mergeInvoiceItems && <span><AlertTriangle size={14}/>Напоминать о счёте</span>}
        {legal.mergeInvoiceItems && <span><Rows3 size={14}/>Счёт одной строкой</span>}
        {!legal.mergeInvoiceItems && legal.invoiceDoctor && <span><DoctorIcon size={14}/>Врач в счёте</span>}
        {!legal.mergeInvoiceItems && legal.invoicePatient===false && <span><UserRoundX size={14}/>Без пациента в счёте</span>}
        {clinic.countDelivery && <span><Truck size={14}/>Доставка в счёте и акте</span>}
      </div>
      <dl className="profile-facts">
        <ProfileFact label="Договор">{legal.contractNo ? `№ ${legal.contractNo}${legal.contractDate ? ` от ${formatDateDots(legal.contractDate)}` : ''}` : legal.contract || 'Не указан'}</ProfileFact>
        <ProfileFact label="Подписант">{[legal.signerPosition,legal.signerName].filter(Boolean).join(', ')}</ProfileFact>
      </dl>
      <details className="profile-details"><summary>Юридические и банковские реквизиты</summary>
        <dl className="profile-facts">
          <ProfileFact label="Организационная форма">{legal.form}</ProfileFact>
          <ProfileFact label="Юридический адрес">{legal.address}</ProfileFact>
          <ProfileFact label="ИНН / КПП">{[legal.inn,legal.kpp].filter(Boolean).join(' / ')}</ProfileFact>
          <ProfileFact label="ОГРН">{legal.ogrn}</ProfileFact>
          <ProfileFact label="Банк">{legal.bank}</ProfileFact>
          <ProfileFact label="БИК">{legal.bik}</ProfileFact>
          <ProfileFact label="Расчётный счёт">{legal.rs}</ProfileFact>
          <ProfileFact label="Корреспондентский счёт">{legal.ks}</ProfileFact>
        </dl>
      </details>
      {legal.note && <div className="profile-note"><span className="profile-label">Примечание</span><p>{legal.note}</p></div>}
      {!branches.some(b=>b.legalId===legal.id) && <p className="mt-3 text-sm" style={{color:'var(--otc-coral)'}}>Нет филиалов — заказы на это юрлицо оформить нельзя</p>}
    </section>)}
  </div>;
}

// ---------------------------------------------------------------------------
// Редактирование
// ---------------------------------------------------------------------------

function ClinicEdit({
  clinic,
  legals,
  branches,
  doctors,
  linkedIds,
  onUpdate,
}: {
  clinic: ClinicRow;
  legals: LegalRow[];
  branches: BranchRow[];
  doctors: { id: string; name: string; clinics: string[] }[];
  linkedIds: string[];
  onUpdate: (kind: string, id: string, patch: Record<string, unknown>) => void;
}) {
  const [confirmLegal, setConfirmLegal] = useState<string | null>(null);
  const [confirmBranch, setConfirmBranch] = useState<string | null>(null);

  const legalToDelete = confirmLegal ? legals.find((l) => l.id === confirmLegal) : undefined;
  const branchToDelete = confirmBranch ? branches.find((b) => b.id === confirmBranch) : undefined;

  const addLegal = () => {
    const legalId = useDrafts.getState().create('clinics', 'legal', {
      clinicId: clinic.id, name: '', form: '', shortForm: '', pay: 'По договору',
      contractNo: '', contractDate: '', contract: '', email: '', inn: '', ogrn: '', kpp: '',
      address: '', bank: '', bik: '', rs: '', ks: '', note: '',
      signer: '', signerPosition: '', signerName: '', signerGenitive: '',
      warnOnReady: false, mergeInvoiceItems: false,
      invoicePatient: true, invoiceDoctor: false,
    });
    // Юрлицо без филиала не на что назначить заказу — создаём парой.
    useDrafts.getState().create('clinics', 'branch', { clinicId: clinic.id, legalId, name: '', address: '' });
  };

  return (
    <div className="space-y-5">
      <Field label="Название клиники">
        <input
          className="otc-input max-w-md"
          value={clinic.name}
          placeholder="Как называют клинику в работе"
          onChange={(e) => onUpdate('clinic', clinic.id, { name: e.target.value })}
        />
      </Field>

      {/* Юрлица */}
      <div>
        <div className="mb-3 flex items-center justify-between gap-3">
          <p className="text-sm font-semibold text-ink">Юридические лица</p>
          <button
            onClick={addLegal}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90"
            style={{ background: 'var(--otc-accent)' }}
          >
            <Plus size={13} /> Добавить юрлицо
          </button>
        </div>

        <div className="space-y-3">
          {legals.map((legal) => (
            <LegalForm
              key={legal.id}
              legal={legal}
              clinic={clinic}
              branches={branches.filter((b) => b.legalId === legal.id)}
              allLegals={legals}
              canDelete={legals.length > 1}
              onConfirmDelete={() => setConfirmLegal(legal.id)}
              canDeleteBranch={branches.length > 1}
              onConfirmDeleteBranch={setConfirmBranch}
              onAddBranch={() =>
                useDrafts.getState().create('clinics', 'branch', {
                  clinicId: clinic.id, legalId: legal.id, name: '', address: '',
                })
              }
              onUpdate={onUpdate}
            />
          ))}
        </div>
      </div>

      {/* Врачи */}
      <div>
        <div className="mb-3 flex items-center justify-between gap-3">
          <p className="text-sm font-semibold text-ink">
            Врачи
            <span className="ml-1.5 text-xs font-normal text-ink2">
              {linkedIds.length > 0 ? `· ${linkedIds.length}` : ''}
            </span>
          </p>
          <DoctorsForClinicPicker
            doctors={doctors as never}
            selected={linkedIds}
            onChange={(next) => onUpdate('clinic', clinic.id, { doctors: next })}
          />
        </div>

        {/*
          Врачи как отдельные метки с обводкой и кнопкой снятия, а не строкой
          текста: так видно, где заканчивается одна фамилия и начинается
          другая, и связь снимается прямо отсюда.
        */}
        <div className="flex flex-wrap gap-2">
          {doctors
            .filter((d) => linkedIds.includes(d.id))
            .map((doctor) => (
              <span
                key={doctor.id}
                className="inline-flex items-center gap-1.5 rounded-full border py-1 pl-3 pr-1.5 text-xs"
                style={{
                  borderColor: 'var(--otc-line)',
                  background: 'var(--otc-surface)',
                  color: 'var(--otc-ink)',
                }}
              >
                {doctor.name}
                <button
                  onClick={() =>
                    onUpdate('clinic', clinic.id, { doctors: linkedIds.filter((id) => id !== doctor.id) })
                  }
                  className="rounded-full p-0.5 transition-colors hover:bg-black/10"
                  title="Отвязать врача"
                >
                  <X size={12} className="text-ink2" />
                </button>
              </span>
            ))}

          {linkedIds.length === 0 && (
            <span
              className="rounded-lg border border-dashed px-3 py-1.5 text-xs"
              style={{ borderColor: 'var(--otc-lightred)', color: 'var(--otc-coral)' }}
            >
              Ни один врач не привязан — в заказе будут доступны все клиники
            </span>
          )}
        </div>
      </div>

      {/* Удаление юрлица — тем же окном, что и удаление клиники */}
      {legalToDelete && (
        <ConfirmDialog
          title={`Удалить юрлицо «${legalToDelete.name}»?`}
          description="Вместе с ним будут удалены его филиалы — филиал без юрлица не определяет плательщика."
          details={
            <>
              <p>
                Филиалов: {branches.filter((b) => b.legalId === legalToDelete.id).length}
              </p>
              <p className="mt-1 text-xs text-ink2">
                Если на юрлицо ссылаются заказы, сервер откажет при сохранении и назовёт их число.
              </p>
            </>
          }
          confirmLabel="Удалить юрлицо"
          onConfirm={() => {
            for (const branch of branches.filter((b) => b.legalId === legalToDelete.id)) {
              useDrafts.getState().remove('clinics', 'branch', branch.id);
            }
            useDrafts.getState().remove('clinics', 'legal', legalToDelete.id);
            setConfirmLegal(null);
          }}
          onCancel={() => setConfirmLegal(null)}
        />
      )}

      {branchToDelete && (
        <ConfirmDialog
          title={`Удалить филиал «${branchToDelete.name}»?`}
          description="Заказы оформляются на филиал — через него определяется юрлицо и способ оплаты."
          details={
            <p className="text-xs text-ink2">
              Если на филиал ссылаются заказы, сервер откажет при сохранении и назовёт их число.
            </p>
          }
          confirmLabel="Удалить филиал"
          onConfirm={() => {
            useDrafts.getState().remove('clinics', 'branch', branchToDelete.id);
            setConfirmBranch(null);
          }}
          onCancel={() => setConfirmBranch(null)}
        />
      )}
    </div>
  );
}

function LegalForm({
  clinic,
  legal,
  branches,
  allLegals,
  canDelete,
  onConfirmDelete,
  canDeleteBranch,
  onConfirmDeleteBranch,
  onAddBranch,
  onUpdate,
}: {
  clinic: ClinicRow;
  legal: LegalRow;
  branches: BranchRow[];
  allLegals: LegalRow[];
  canDelete: boolean;
  onConfirmDelete: () => void;
  canDeleteBranch: boolean;
  onConfirmDeleteBranch: (branchId: string) => void;
  onAddBranch: () => void;
  onUpdate: (kind: string, id: string, patch: Record<string, unknown>) => void;
}) {
  const set = (patch: Record<string, unknown>) => onUpdate('legal', legal.id, patch);

  /**
   * Номер и дата договора подставляют готовую строку-подпись.
   *
   * Сами документы её НЕ используют: счёт печатает «Основной Договор № N»,
   * акт — «по договору № N от ДД.ММ.ГГГГ г.», и оба собираются из номера
   * и даты напрямую. Строка осталась как свободная формулировка на случаи
   * вроде «Доп. соглашение №2» или «Без договора», поэтому при очищенном
   * номере она не трогается: стирать вписанное вручную очисткой соседнего
   * поля неправильно.
   */
  const setContract = (patch: { contractNo?: string; contractDate?: string }) => {
    /*
      Строку собирает общий `contractText` — тот же, которым пользуется сервер.
      Раньше формат был записан здесь отдельно, и правка номера из вкладки
      «Договоры» строку не трогала: в счёте печаталось основание со старым
      номером. Сервер теперь пересобирает её при любой правке, а здесь она
      обновляется сразу, чтобы поле не отставало от набранного.
    */
    const merged = { ...legal, ...patch };
    set({ ...patch, contract: contractText(merged.contractNo, merged.contractDate) });
  };

  /* В правке лист тот же белый, что и в просмотре. */
  return (
    <div className="rounded-lg border p-3" style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}>
      {/*
        Заголовок с кнопкой удаления. Раньше кнопка стояла внизу карточки и
        визуально прилипала к следующему юрлицу — было неясно, что именно
        удаляешь. В заголовке принадлежность однозначна.
      */}
      <div className="mb-3 flex items-center justify-between gap-2 border-b pb-2" style={{ borderColor: 'var(--otc-line)' }}>
        <p className="min-w-0 truncate text-xs font-semibold uppercase tracking-wide text-ink2">
          {legalTitleText(legal)}
        </p>

        {canDelete && (
          <button
            onClick={onConfirmDelete}
            className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors hover:bg-black/5"
            style={{
              borderColor: 'var(--otc-line)',
              background: 'var(--otc-surface)',
              color: 'var(--otc-coral)',
            }}
            title="Удалить юрлицо"
          >
            <Trash2 size={12} /> Удалить юрлицо
          </button>
        )}
      </div>

      <div className="mb-3 grid gap-3 md:grid-cols-3">
        <Field label="Форма (кратко)">
          <input className="otc-input" value={legal.shortForm} placeholder="ООО / ИП"
                 onChange={(e) => set({ shortForm: e.target.value })} />
        </Field>
        <Field
          label="Название"
          hint={
            legal.name ? `В документах: ${legalTitleText(legal)}` : undefined
          }
        >
          <input
            className="otc-input"
            value={legal.name}
            placeholder="Как в реквизитах"
            onChange={(e) => set({ name: e.target.value })}
          />
        </Field>
        <Field label="Способ оплаты" hint="В заказе можно переопределить">
          <select className="otc-input" value={legal.pay} onChange={(e) => set({ pay: e.target.value })}>
            <option>По договору</option>
            <option>Наличные</option>
          </select>
        </Field>
      </div>

      <div className="mb-3 grid gap-3 md:grid-cols-2">
        <Field label="Организационная форма (полная)">
          <input
            className="otc-input"
            value={legal.form}
            placeholder="Общество с ограниченной ответственностью"
            onChange={(e) => set({ form: e.target.value })}
          />
        </Field>
        <Field label="Почта для счетов">
          <input className="otc-input" value={legal.email} onChange={(e) => set({ email: e.target.value })} />
        </Field>
      </div>

      <div className="mb-3 grid gap-3 md:grid-cols-3">
        <Field label="ИНН"><input className="otc-input tabular" value={legal.inn} onChange={(e) => set({ inn: e.target.value })} /></Field>
        <Field label="КПП"><input className="otc-input tabular" value={legal.kpp} onChange={(e) => set({ kpp: e.target.value })} /></Field>
        <Field label="ОГРН"><input className="otc-input tabular" value={legal.ogrn} onChange={(e) => set({ ogrn: e.target.value })} /></Field>
      </div>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-3 border-t pt-3" style={{borderColor:'var(--otc-line)'}}>
        <h4 className="flex items-center gap-2 text-sm font-semibold"><FileText size={17} aria-hidden="true"/>Реквизиты существующего договора</h4>
      </div>
      <p className="mb-3 text-xs text-ink2">Заполните, если договор уже заключён. Для создания нового используйте кнопку ниже.</p>
      <div className="mb-3 grid gap-3 md:grid-cols-3">
        <Field label="№ договора">
          <input
            className="otc-input"
            value={legal.contractNo}
            placeholder="Номер не указан"
            onChange={(e) => setContract({ contractNo: e.target.value })}
          />
        </Field>
        {/*
          Дата договора — календарное поле, а не свободная строка.
          Раньше она хранилась фразой («05 июня 2025»), и акт не мог сослаться
          на договор в положенном виде «от 05.06.2025 г.»: из фразы дату
          не получить без разбора. Как её печатать — решает шаблон документа.
        */}
        <Field
          label="Дата договора"
          // Подсказка показывает РЕАЛЬНУЮ дату этого юрлица, а не образец:
          // застывший пример читался как значение поля и вводил в заблуждение.
          hint={
            legal.contractDate
              ? `В акт уйдёт как ${formatDateDots(legal.contractDate)} г.`
              : undefined
          }
        >
          <input
            type="date"
            className="otc-input tabular"
            value={legal.contractDate}
            onChange={(e) => setContract({ contractDate: e.target.value })}
          />
        </Field>
        <Field
          label="Основание для документов"
          hint="Собирается из номера и даты, но можно исправить вручную"
        >
          {/*
            Строка печатается в счёте как «Основание». Раньше она только
            собиралась автоматически и не правилась — а формулировки в договорах
            встречаются разные: «Договор №19 от 05.06.2025», «Доп. соглашение
            №2», «Без договора». Теперь номер и дата её подставляют, но
            последнее слово за пользователем.
          */}
          <input
            className="otc-input"
            value={legal.contract}
            placeholder="Основание не указано"
            onChange={(e) => set({ contract: e.target.value })}
          />
        </Field>
      </div>
      <div className="mb-4"><ClinicContractLink legalId={legal.id}/></div>

      <div className="mb-3">
        <Field label="Адрес">
          <input className="otc-input" value={legal.address} onChange={(e) => set({ address: e.target.value })} />
        </Field>
      </div>

      <div className="mb-3 grid gap-3 md:grid-cols-4">
        <Field label="Банк"><input className="otc-input" value={legal.bank} onChange={(e) => set({ bank: e.target.value })} /></Field>
        <Field label="БИК"><input className="otc-input tabular" value={legal.bik} onChange={(e) => set({ bik: e.target.value })} /></Field>
        <Field label="Расчётный счёт"><input className="otc-input tabular" value={legal.rs} onChange={(e) => set({ rs: e.target.value })} /></Field>
        <Field label="Корр. счёт"><input className="otc-input tabular" value={legal.ks} onChange={(e) => set({ ks: e.target.value })} /></Field>
      </div>

      {/*
        Подписант. Должность и ФИО хранятся в ИМЕНИТЕЛЬНОМ падеже, а падежи
        для акта выводятся правилами: раньше здесь лежала одна строка вида
        «генерального директора Стародубцевой Анны Евгеньевны (А.Е. Стародубцева)»,
        и в карточке она читалась как безграмотный набор слов.

        Ниже показано, что именно уйдёт в акт: правила склонения покрывают
        не всё, и увидеть результат надо здесь, а не в отправленном документе.
      */}
      <div className="mb-2 grid gap-3 md:grid-cols-2">
        <Field label="Должность подписанта" hint="Как называется должность. Пусто — ИП действует лично">
          <input
            className="otc-input"
            value={legal.signerPosition}
            onChange={(e) => set({ signerPosition: e.target.value })}
            placeholder="Генеральный директор"
          />
        </Field>
        <Field label="ФИО подписанта" hint="Фамилия, имя и отчество полностью">
          <input
            className="otc-input"
            value={legal.signerName}
            onChange={(e) => set({ signerName: e.target.value })}
            placeholder="Фамилия Имя Отчество"
          />
        </Field>
      </div>

      <SignerPreview legal={legal} onOverride={(value) => set({ signerGenitive: value })} />

      <div className="mb-3 mt-3">
        <Field label="Примечание">
          <input className="otc-input" value={legal.note} onChange={(e) => set({ note: e.target.value })} />
        </Field>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {/*
          Напоминание о счёте не уживается со «сводить в одну строку»: такие
          клиники платят за месяц целиком, и значок у каждого готового заказа
          напоминал бы о том, чего делать не нужно. Включили одну строку —
          напоминание выключается само и становится недоступным.
        */}
        <ToggleSetting
          checked={legal.warnOnReady && !legal.mergeInvoiceItems}
          onChange={(next) => set({ warnOnReady: next })}
          disabled={legal.mergeInvoiceItems}
          label="Предупреждать, что пора выставить счёт"
          hint={
            legal.mergeInvoiceItems
              ? 'Не действует: работы сводятся в одну строку'
              : 'Значок появится у готовых заказов по договору, пока счёт не выставлен'
          }
          accent="var(--otc-amber)"
        />
        <ToggleSetting
          checked={legal.mergeInvoiceItems}
          onChange={(next) => set(next ? { mergeInvoiceItems: true, warnOnReady: false } : { mergeInvoiceItems: false })}
          label="Сводить работы в одну строку"
          hint="В счёте и акте будет одна позиция «Изготовление ортодонтических протезов (аппаратов)» с общей суммой — для клиник, платящих за месяц целиком"
          accent="var(--otc-blue)"
        />
        {/*
          Фамилии в строке счёта. Пациент включён у всех — так выставлялись
          все счета; врача включают тем клиникам, что сверяют счёт по врачам.
          В окне нового счёта оба можно переключить разово.

          При «одной строке» фамилий нет вовсе: вся работа — одна позиция без
          пациентов и врачей. Тогда выключатели видны, но не нажимаются, а
          подсказка говорит почему, — иначе казалось бы, что они сломаны.
        */}
        <ToggleSetting
          checked={!legal.mergeInvoiceItems && (legal.invoicePatient ?? true)}
          onChange={(next) => set({ invoicePatient: next })}
          disabled={legal.mergeInvoiceItems}
          label="ФИО пациента в счёте"
          hint={legal.mergeInvoiceItems ? 'Не действует: работы сводятся в одну строку' : 'В строке счёта и акта: «…, пац. Иванова М.П.»'}
          accent="var(--otc-blue)"
        />
        <ToggleSetting
          checked={!legal.mergeInvoiceItems && (legal.invoiceDoctor ?? false)}
          onChange={(next) => set({ invoiceDoctor: next })}
          disabled={legal.mergeInvoiceItems}
          label="ФИО врача в счёте"
          hint={legal.mergeInvoiceItems ? 'Не действует: работы сводятся в одну строку' : 'После пациента, через запятую: «…, пац. Иванова М.П., врач Гущина Е.Д.»'}
          accent="var(--otc-blue)"
        />
        <div className="space-y-3">
          <ToggleSetting
            checked={clinic.countDelivery ?? false}
            onChange={(next) => onUpdate('clinic', clinic.id, { countDelivery: next })}
            label="Считать доставку"
            hint="Добавлять второй строкой в каждый новый счёт и акт. Настройка для всей клиники."
          />
          {clinic.countDelivery && (
            <Field label="Стоимость доставки, ₽">
              <input
                type="number"
                min="0"
                max="21474836.47"
                step="0.01"
                className="otc-input tabular max-w-48"
                value={(clinic.deliveryKop ?? 0) / 100}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  if (Number.isFinite(value) && value >= 0 && value <= 21474836.47) {
                    onUpdate('clinic', clinic.id, { deliveryKop: rublesToKop(value) });
                  }
                }}
              />
            </Field>
          )}
        </div>
      </div>

      {/* Филиалы этого юрлица */}
      <div className="mt-3 border-t pt-3" style={{ borderColor: 'var(--otc-line)' }}>
        <div className="mb-2 flex items-center justify-between gap-3">
          <p className="text-sm font-semibold text-ink">Филиалы</p>
          <button
            onClick={onAddBranch}
            className="inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors hover:bg-black/5"
            style={{ borderColor: 'var(--otc-accent)', color: 'var(--otc-accent)', background: 'var(--otc-surface)' }}
          >
            <Plus size={13} /> Добавить филиал
          </button>
        </div>

        {branches.map((branch) => (
          /*
            Равняем по ВЕРХУ, а не по низу.

            У «Фактического адреса» под полем есть пояснение, и при выравнивании
            по нижнему краю оно поднимало само поле выше соседних — три поля
            в строке стояли ступенькой. Подписи у всех трёх однострочные, так
            что по верху поля встают ровно, а пояснение просто свисает вниз.
          */
          <div key={branch.id} className="mb-2 flex flex-wrap items-start gap-2">
            <div className="min-w-[10rem] flex-1">
              <Field label="Название филиала">
                <input
                  className="otc-input"
                  value={branch.name}
                  placeholder="Как называют точку"
                  onChange={(e) => onUpdate('branch', branch.id, { name: e.target.value })}
                />
              </Field>
            </div>
            <div className="min-w-[14rem] flex-[2]">
              <Field label="Фактический адрес" hint="Куда везти работу — может отличаться от юридического">
                <input
                  className="otc-input"
                  value={branch.address}
                  placeholder="Город, улица, дом"
                  onChange={(e) => onUpdate('branch', branch.id, { address: e.target.value })}
                />
              </Field>
            </div>
            <div className="min-w-[9rem] flex-1">
              <Field label="Юрлицо">
                <select
                  className="otc-input"
                  value={branch.legalId}
                  onChange={(e) => onUpdate('branch', branch.id, { legalId: e.target.value })}
                >
                  {allLegals.map((option) => (
                    <option key={option.id} value={option.id}>{option.name}</option>
                  ))}
                </select>
              </Field>
            </div>

            {/* Отступ сверху — ровно высота подписи поля (12/16 px плюс
                зазор): корзина встаёт вровень с полями, а не с их подписями. */}
            <div className="mt-5">
              {canDeleteBranch && (
                <button
                  onClick={() => onConfirmDeleteBranch(branch.id)}
                  className="rounded-lg border p-2 transition-colors hover:bg-black/5"
                  style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
                  title="Удалить филиал"
                >
                  <Trash2 size={13} style={{ color: 'var(--otc-coral)' }} />
                </button>
              )}
            </div>
          </div>
        ))}

        {branches.length === 0 && (
          <p className="text-xs" style={{ color: 'var(--otc-coral)' }}>
            У юрлица нет филиалов — заказы на него оформить нельзя
          </p>
        )}
      </div>
    </div>
  );
}
